// src/pages/sessions/components/LiveSession/Chat/SessionChat.jsx
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, X, File, ExternalLink, Upload } from 'lucide-react';
import { ArrowUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { downloadFile } from '@/utils/downloadFile';
import { format } from 'date-fns';
import { useRoomContext } from '@livekit/components-react';
import { DataPacket_Kind, RoomEvent } from 'livekit-client';
import { toast } from 'sonner';
import useAuthStore from '@/stores/authStore';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import sessionService from '@/services/sessionService';
import { uploadSessionChatAttachment, getOneDriveMetadata, getGoogleDriveMetadata } from '@/services/api';

// Fixed cleanup tracker for SessionChat with proper cleanup order
class SessionChatCleanup {
  constructor() {
    this.timeouts = new Set();
    this.intervals = new Set();
    this.abortControllers = new Set();
    this.eventListeners = new Map();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  setInterval(fn, delay) {
    if (this.disposed) return null;
    const id = setInterval(() => {
      if (!this.disposed) fn();
    }, delay);
    this.intervals.add(id);
    return id;
  }

  createAbortController() {
    if (this.disposed) return null;
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  addEventListener(target, event, handler, options) {
    if (this.disposed || !target) return;
    
    target.addEventListener(event, handler, options);
    const key = `${event}-${Date.now()}-${Math.random()}`;
    this.eventListeners.set(key, { target, event, handler, options });
  }

  cleanup() {
    this.disposed = true;
    
    this.eventListeners.forEach(({ target, event, handler, options }) => {
      try {
        if (target && typeof target.removeEventListener === 'function') {
          target.removeEventListener(event, handler, options);
        }
      } catch (e) {
        console.debug('Error removing event listener:', e);
      }
    });
    this.eventListeners.clear();
    
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {
        console.debug('Error aborting controller:', e);
      }
    });
    this.abortControllers.clear();
    
    this.intervals.forEach(id => {
      try {
        clearInterval(id);
      } catch (e) {
        console.debug('Error clearing interval:', e);
      }
    });
    this.intervals.clear();
    
    this.timeouts.forEach(id => {
      try {
        clearTimeout(id);
      } catch (e) {
        console.debug('Error clearing timeout:', e);
      }
    });
    this.timeouts.clear();
  }
}

// Throttle utility
function throttle(func, wait) {
  let timeout = null;
  let previous = 0;
  
  const throttled = function() {
    const now = Date.now();
    const remaining = wait - (now - previous);
    const args = arguments;
    const context = this;
    
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func.apply(context, args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func.apply(context, args);
      }, remaining);
    }
  };
  
  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    previous = 0;
  };
  
  return throttled;
}

// Text encoder/decoder instances
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const SessionChat = memo(({ sessionId, onUnreadCountChange, isActive }) => {
  const { t } = useTranslation();
  const { user, institutionMemberId } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const [detectedLinks, setDetectedLinks] = useState([]);
  const [linkMetadata, setLinkMetadata] = useState({});
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessageId, setLastReadMessageId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const dropZoneRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  const cleanupRef = useRef(new SessionChatCleanup());
  const cleanup = cleanupRef.current;
  
  const mountedRef = useRef(true);
  const isAtBottomRef = useRef(true);
  
  const hasLoadedHistoryRef = useRef(false);
  const isActiveRef = useRef(isActive);
  const sessionIdRef = useRef(sessionId);
  const userRef = useRef(user);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const MESSAGE_CHAR_LIMIT = 1000;

  const linkRegex = /(https?:\/\/[^\s]+)/g;
  const oneDriveRegex = /https:\/\/(1drv\.ms\/|onedrive\.live\.com)/;
  const googleDriveRegex = /https:\/\/(drive\.google\.com|docs\.google\.com)/;

  const room = useRoomContext();
  const { playDirectMessage } = useSoundEffects();

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  useEffect(() => {
    if (isActive && messages.length > 0 && isAtBottomRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.id !== lastReadMessageId) {
        setLastReadMessageId(lastMessage.id);
        setUnreadCount(0);
      }
    }
  }, [isActive, messages, lastReadMessageId]);

  // Document-level drag and drop prevention
  useEffect(() => {
    const preventBrowserDefault = (e) => {
      const dropZone = dropZoneRef.current;
      if (dropZone && dropZone.contains(e.target)) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('dragover', preventBrowserDefault, false);
    document.addEventListener('drop', preventBrowserDefault, false);

    return () => {
      document.removeEventListener('dragover', preventBrowserDefault, false);
      document.removeEventListener('drop', preventBrowserDefault, false);
    };
  }, []);

  // NATIVE DRAG AND DROP HANDLERS
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    
    if (!dropZone) {
      return;
    }

    const handleNativeDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleNativeDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    };

    const handleNativeDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dropZone.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleNativeDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) {
        return;
      }
      
      const file = files[0];
      
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(t('chat.file.tooLarge', { size: sizeMB }));
        return;
      }
      
      setAttachedFile(file);
      toast.success(t('chat.file.ready', { name: file.name }));
    };

    dropZone.addEventListener('dragenter', handleNativeDragEnter, false);
    dropZone.addEventListener('dragover', handleNativeDragOver, false);
    dropZone.addEventListener('dragleave', handleNativeDragLeave, false);
    dropZone.addEventListener('drop', handleNativeDrop, false);

    return () => {
      dropZone.removeEventListener('dragenter', handleNativeDragEnter, false);
      dropZone.removeEventListener('dragover', handleNativeDragOver, false);
      dropZone.removeEventListener('dragleave', handleNativeDragLeave, false);
      dropZone.removeEventListener('drop', handleNativeDrop, false);
    };
  }, [MAX_FILE_SIZE, loadingHistory, t]);

  const checkIfAtBottom = useCallback(
    throttle(() => {
      if (!mountedRef.current || !messagesContainerRef.current) return;
      
      // In column-reverse, scrollTop === 0 means we are at the visual bottom
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollTop < 50;
      isAtBottomRef.current = isAtBottom;
      
      if (isAtBottom && isActiveRef.current && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.id !== lastReadMessageId) {
          setLastReadMessageId(lastMessage.id);
          setUnreadCount(0);
        }
      }
    }, 100),
    [messages, lastReadMessageId]
  );

  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload, participant, kind) => {
      if (!mountedRef.current) return;
      
      try {
        let messageString;
        
        if (typeof payload === 'string') {
          messageString = payload;
        } else if (payload instanceof ArrayBuffer || payload instanceof Uint8Array) {
          messageString = decoder.decode(payload);
        } else {
          console.error('Unexpected payload type:', typeof payload);
          return;
        }
        
        const parsed = JSON.parse(messageString);
        
        if (parsed.channel !== 'session-chat') return;
        
        switch (parsed.type) {
          case 'message':
            if (parsed.message?.sender?.id === userRef.current.id) {
              return;
            }
            
            setMessages(prev => {
              const exists = prev.some(m => m.id === parsed.message.id);
              if (!exists) {
                // Play notification sound for incoming messages
                playDirectMessage();
                
                if (!isActiveRef.current || !isAtBottomRef.current) {
                  setUnreadCount(prevCount => prevCount + 1);
                  
                  const senderName = parsed.message.sender?.name || t('videoGrid.someone');
                  const messageContent = parsed.message.content || '';
                  const previewText = messageContent.length > 50 
                    ? messageContent.substring(0, 47) + '...' 
                    : messageContent;
                  
                  toast.info(
                    <div className="flex flex-col">
                      <span className="font-semibold">{senderName}</span>
                      <span className="text-sm">{previewText}</span>
                    </div>,
                    {
                      duration: 3000,
                      position: 'top-right'
                    }
                  );
                } else {
                  setLastReadMessageId(parsed.message.id);
                }
                
                return [...prev, parsed.message];
              }
              return prev;
            });
            break;
            
          case 'typing':
            if (parsed.userId !== userRef.current.id) {
              setTypingUsers(prev => new Set(prev).add(parsed.userId));
              
              cleanup.setTimeout(() => {
                if (mountedRef.current) {
                  setTypingUsers(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(parsed.userId);
                    return newSet;
                  });
                }
              }, 3000);
            }
            break;
        }
      } catch (error) {
        console.error('Failed to parse chat message:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, cleanup, t, playDirectMessage]);

  const sendChatData = useCallback(async (messageData) => {
    if (!room || !room.localParticipant || !mountedRef.current) {
      console.error('Room or localParticipant not available');
      return;
    }

    try {
      const dataToSend = {
        ...messageData,
        channel: 'session-chat'
      };
      
      const jsonString = JSON.stringify(dataToSend);
      const data = encoder.encode(jsonString);
      
      await room.localParticipant.publishData(data, DataPacket_Kind.RELIABLE);
    } catch (error) {
      console.error('Failed to send chat data:', error);
    }
  }, [room]);

  const detectLinks = useCallback(async (text) => {
    if (!mountedRef.current) return;
    
    const matches = text.match(linkRegex);
    if (matches) {
      setDetectedLinks(matches);
      
      for (const url of matches) {
        const abortController = cleanup.createAbortController();
        if (!abortController) return;
        
        if (oneDriveRegex.test(url)) {
          try {
            const metadata = await getOneDriveMetadata(url, { signal: abortController.signal });
            
            if (!mountedRef.current) return;
            
            if (metadata.success) {
              setLinkMetadata(prev => ({
                ...prev,
                [url]: {
                  type: 'onedrive',
                  title: metadata.data.filename || t('chat.file.oneDriveDoc'),
                  icon: '📄'
                }
              }));
            }
          } catch (error) {
            if (error.name !== 'AbortError' && mountedRef.current) {
              console.warn('Failed to fetch OneDrive metadata:', error);
            }
          }
        } else if (googleDriveRegex.test(url)) {
          try {
            const metadata = await getGoogleDriveMetadata(url, { signal: abortController.signal });
            
            if (!mountedRef.current) return;
            
            if (metadata.success) {
              setLinkMetadata(prev => ({
                ...prev,
                [url]: {
                  type: 'googledrive',
                  title: metadata.data.filename || t('chat.file.googleDoc'),
                  icon: '📄'
                }
              }));
            }
          } catch (error) {
            if (error.name !== 'AbortError' && mountedRef.current) {
              console.warn('Failed to fetch Google Drive metadata:', error);
            }
          }
        }
      }
    } else {
      setDetectedLinks([]);
    }
  }, [cleanup, t]);

  useEffect(() => {
    const timeoutId = cleanup.setTimeout(() => {
      detectLinks(newMessage);
    }, 500);
    
    return () => {};
  }, [newMessage, detectLinks, cleanup]);

  const scrollToBottom = useCallback((force = false) => {
    if (!mountedRef.current) return;
    
    if (force || isAtBottomRef.current) {
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        // In column-reverse, the visual bottom is scrollTop === 0
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }
      });
      
      if (isActiveRef.current && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.id !== lastReadMessageId) {
          setLastReadMessageId(lastMessage.id);
          setUnreadCount(0);
        }
      }
    }
  }, [messages, lastReadMessageId]);

  useEffect(() => {
    scrollToBottom();
    cleanup.setTimeout(() => scrollToBottom(), 50);
    cleanup.setTimeout(() => scrollToBottom(), 100);
  }, [messages, scrollToBottom, cleanup]);

  useEffect(() => {
    if (hasLoadedHistoryRef.current) return;
    
    const loadChatHistory = async () => {
      if (!mountedRef.current) return;
      
      try {
        setLoadingHistory(true);
        const response = await sessionService.getSessionChatHistory(sessionIdRef.current);
        
        if (!mountedRef.current) return;
        
        if (response.success && response.data) {
          const formattedMessages = response.data.map(msg => ({
            id: msg.id,
            sender: {
              id: msg.sender_user_id,
              name: msg.sender_name,
              institutionMemberId: msg.sender_im_id,
              isTeacher: msg.sender_role === 'teacher'
            },
            content: msg.message_content || msg.message || msg.messageContent,
            timestamp: new Date(msg.sent_at || msg.timestamp || msg.created_at),
            type: 'text',
            isPersisted: true,
            attachment: msg.attachment_url ? {
              name: msg.attachment_name,
              type: msg.attachment_type,
              size: msg.attachment_size_kb,
              url: msg.attachment_url
            } : null
          }));
          
          setMessages(formattedMessages);
          hasLoadedHistoryRef.current = true;
          
          if (formattedMessages.length > 0 && isActiveRef.current) {
            setLastReadMessageId(formattedMessages[formattedMessages.length - 1].id);
          }
        }
      } catch (error) {
        if (mountedRef.current) {
          console.error('Failed to load chat history:', error);
        }
      } finally {
        if (mountedRef.current) {
          setLoadingHistory(false);
        }
      }
    };

    loadChatHistory();
  }, []);

  const formatMessageContent = useCallback((content) => {
    if (!content) return null;
    
    const parts = content.split(linkRegex);
    const matches = content.match(linkRegex) || [];
    
    return parts.map((part, index) => {
      if (matches.includes(part)) {
        const metadata = linkMetadata[part];
        
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline break-all"
          >
            {metadata ? (
              <>
                <span>{metadata.icon}</span>
                <span>{metadata.title}</span>
              </>
            ) : (
              <>
                <ExternalLink className="w-3 h-3 inline" />
                <span>{part}</span>
              </>
            )}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  }, [linkMetadata]);

  const handleFileSelect = useCallback((event) => {
    if (!mountedRef.current) return;
    
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('chat.file.maxSize'));
        return;
      }
      setAttachedFile(file);
    }
  }, [t]);

  const removeAttachedFile = useCallback(() => {
    if (!mountedRef.current) return;
    
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handlePaste = useCallback((e) => {
    if (!mountedRef.current) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('chat.file.maxSize'));
          return;
        }
        setAttachedFile(file);
        return;
      }
    }
  }, [MAX_FILE_SIZE, t]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !attachedFile) || sendingMessage || !mountedRef.current) return;

    // CLEAR TYPING INDICATOR IMMEDIATELY
    setIsTyping(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    const tempId = `temp-${Date.now()}`;
    const currentUser = userRef.current;
    const message = {
      id: tempId,
      sender: {
        id: currentUser.id,
        name: currentUser.fullName || currentUser.displayName || currentUser.display_name || currentUser.name,
        institutionMemberId: institutionMemberId,
        isTeacher: currentUser.role === 'teacher' || currentUser.userRole === 'teacher'
      },
      content: newMessage.trim(),
      timestamp: new Date(),
      type: 'text',
      isPending: true,
      attachment: null
    };

    try {
      setSendingMessage(true);
      
      let attachmentData = null;
      
      if (attachedFile) {
        setIsUploadingFile(true);
        try {
          const formData = new FormData();
          formData.append('file', attachedFile);
          
          const uploadResponse = await uploadSessionChatAttachment(sessionIdRef.current, formData, (progress) => {
            if (mountedRef.current) {
              setUploadProgress(progress);
            }
          });
          
          if (!mountedRef.current) return;
          
          if (uploadResponse.success) {
            let fileType = uploadResponse.data.fileType || attachedFile.type;
            
            const typeMapping = {
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'application/docx',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'application/xlsx',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'application/pptx',
              'application/vnd.ms-excel': 'application/excel',
              'application/vnd.ms-powerpoint': 'application/ppt',
              'application/vnd.ms-word': 'application/word',
              'application/vnd.oasis.opendocument.text': 'application/odt',
              'application/vnd.oasis.opendocument.spreadsheet': 'application/ods',
              'application/vnd.oasis.opendocument.presentation': 'application/odp'
            };
            
            fileType = typeMapping[fileType] || fileType;
            if (fileType.length > 50) {
              fileType = fileType.substring(0, 50);
            }
            
            attachmentData = {
              attachmentName: uploadResponse.data.fileName,
              attachmentType: fileType,
              attachmentSizeKb: Math.round(uploadResponse.data.fileSize / 1024),
              attachmentUrl: uploadResponse.data.fileUrl
            };
            
            message.attachment = {
              name: uploadResponse.data.fileName,
              type: fileType,
              size: Math.round(uploadResponse.data.fileSize / 1024),
              url: uploadResponse.data.fileUrl
            };
          } else {
            throw new Error(uploadResponse.message || t('chat.file.uploadFailed'));
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          if (mountedRef.current) {
            toast.error(t('chat.file.uploadError'));
            setIsUploadingFile(false);
            setUploadProgress(0);
            setSendingMessage(false);
          }
          return;
        } finally {
          if (mountedRef.current) {
            setIsUploadingFile(false);
            setUploadProgress(0);
          }
        }
      }
      
      if (!mountedRef.current) return;
      
      setMessages(prev => [...prev, message]);
      setNewMessage('');
      removeAttachedFile();
      setDetectedLinks([]);
      setLinkMetadata({});
      
      cleanup.setTimeout(() => scrollToBottom(true), 100);

      const messageData = {
        ...(message.content && { messageContent: message.content }),
        ...(attachmentData && {
          attachmentName: attachmentData.attachmentName,
          attachmentType: attachmentData.attachmentType,
          attachmentSizeKb: attachmentData.attachmentSizeKb,
          attachmentUrl: attachmentData.attachmentUrl
        })
      };
      
      // DEBUG LOGGING
      console.log('=== MESSAGE DATA DEBUG ===');
      console.log('message.content:', message.content);
      console.log('attachmentData:', attachmentData);
      console.log('messageData:', JSON.stringify(messageData, null, 2));
      console.log('========================');
      
      const response = await sessionService.sendSessionChatMessage(sessionIdRef.current, messageData);

      if (!mountedRef.current) return;

      if (response.success) {
        const finalMessage = {
          ...message,
          id: response.data.id,
          isPending: false,
          isPersisted: true,
          // Update attachment URL with presigned URL from response
          ...(response.data.attachment_url && {
            attachment: {
              ...message.attachment,
              url: response.data.attachment_url
            }
          })
        };
        
        setMessages(prev => prev.map(m => 
          m.id === tempId ? finalMessage : m
        ));
        
        setLastReadMessageId(response.data.id);

        await sendChatData({
          type: 'message',
          message: finalMessage
        });
        
        if (!mountedRef.current) return;
      } else {
        throw new Error(response.message || t('chat.sendFailed'));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      if (mountedRef.current) {
        if (error.message?.includes('Validation')) {
          toast.error(t('chat.invalidFormat'));
        } else {
          toast.error(error.message || t('chat.sendError'));
        }
        
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setNewMessage(message.content);
      }
    } finally {
      if (mountedRef.current) {
        setSendingMessage(false);
        requestAnimationFrame(() => {
          if (inputRef.current) inputRef.current.focus();
        });
      }
    }
  }, [newMessage, attachedFile, sendingMessage, institutionMemberId, removeAttachedFile, scrollToBottom, sendChatData, cleanup, t]);

  const handleTyping = useCallback((e) => {
    if (!mountedRef.current) return;
    
    const newValue = e.target.value;
    if (newValue.length <= MESSAGE_CHAR_LIMIT) {
      setNewMessage(newValue);
      
      // Clear existing typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      if (!isTyping && newValue.trim()) {
        setIsTyping(true);
        const currentUser = userRef.current;
        sendChatData({
          type: 'typing',
          userId: currentUser.id,
          userName: currentUser.fullName || currentUser.displayName || currentUser.display_name || currentUser.name
        });
      }

      // Set new timeout to clear typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setIsTyping(false);
        }
      }, 2000);
    }
  }, [isTyping, sendChatData]);

  const getTypingUsersText = useCallback(() => {
    const typingUsersList = Array.from(typingUsers);
    if (typingUsersList.length === 0) return null;
    if (typingUsersList.length === 1) return t('chat.typing.someone');
    return t('chat.typing.multiple', { count: typingUsersList.length });
  }, [typingUsers, t]);

  // Render messages without date separators
  const renderMessages = useCallback(() => {
    return messages.map((message) => {
      const isOwn = message.sender.id === userRef.current.id;
      
      return (
        <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2.5`}>
          <div className={`max-w-[75%] min-w-0 ${isOwn ? 'order-2' : 'order-1'}`}>
            {!isOwn && (
              <p className="text-xs text-gray-400 mb-1">
                {message.sender.name}
                {message.sender.isTeacher && (
                  <span className="ml-1 px-1.5 py-0.5 bg-cyan-600 text-white text-xs rounded">
                    {t('roles.teacher')}
                  </span>
                )}
              </p>
            )}

            <div
              className={`inline-block max-w-full px-3 py-2 rounded-lg ${
                isOwn
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-700 text-white'
              } ${message.isPending ? 'opacity-70' : ''}`}
            >
              {message.content && (
                <p className="text-sm break-words whitespace-pre-wrap leading-relaxed">
                  {formatMessageContent(message.content)}
                </p>
              )}
              
              {message.attachment && (
                <div className={`mt-2 p-2 rounded flex items-center gap-2 ${
                  isOwn ? 'bg-cyan-700' : 'bg-gray-600'
                }`}>
                  <File className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{message.attachment.name}</p>
                    <p className="text-xs opacity-70">
                      {message.attachment.size ? `${message.attachment.size} KB` : t('chat.file.file')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadFile(message.attachment.url, message.attachment.name)}
                    className={`p-1.5 rounded flex-shrink-0 transition-colors ${
                      isOwn ? 'hover:bg-cyan-600' : 'hover:bg-gray-500'
                    }`}
                    title={t('chat.download', 'Download')}
                    aria-label={t('chat.download', 'Download')}
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              <p className={`text-xs mt-1 ${
                isOwn ? 'text-cyan-200 text-right' : 'text-gray-400'
              }`}>
                {format(message.timestamp, 'HH:mm')}
              </p>
            </div>
          </div>
        </div>
      );
    });
  }, [messages, formatMessageContent, t]);

  // Reversed render for column-reverse container — cancels the visual flip
  const renderMessagesReversed = useCallback(() => {
    return [...messages].reverse().map((message) => {
      const isOwn = message.sender.id === userRef.current.id;
      return (
        <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2.5`}>
          <div className={`max-w-[75%] min-w-0 ${isOwn ? 'order-2' : 'order-1'}`}>
            {!isOwn && (
              <p className="text-xs text-gray-400 mb-1">
                {message.sender.name}
                {message.sender.isTeacher && (
                  <span className="ml-1 px-1.5 py-0.5 bg-cyan-600 text-white text-xs rounded">
                    {t('roles.teacher')}
                  </span>
                )}
              </p>
            )}
            <div className={`inline-block max-w-full px-3 py-2 rounded-lg ${isOwn ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-white'} ${message.isPending ? 'opacity-70' : ''}`}>
              {message.content && (
                <p className="text-sm break-words whitespace-pre-wrap leading-relaxed">
                  {formatMessageContent(message.content)}
                </p>
              )}
              {message.attachment && (
                <div className={`mt-2 p-2 rounded flex items-center gap-2 ${isOwn ? 'bg-cyan-700' : 'bg-gray-600'}`}>
                  <File className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{message.attachment.name}</p>
                    <p className="text-xs opacity-70">
                      {message.attachment.size ? `${message.attachment.size} KB` : t('chat.file.file')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadFile(message.attachment.url, message.attachment.name)}
                    className={`p-1.5 rounded flex-shrink-0 transition-colors ${isOwn ? 'hover:bg-cyan-600' : 'hover:bg-gray-500'}`}
                    title={t('chat.download', 'Download')}
                    aria-label={t('chat.download', 'Download')}
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className={`text-xs mt-1 ${isOwn ? 'text-cyan-200 text-right' : 'text-gray-400'}`}>
                {format(message.timestamp, 'HH:mm')}
              </p>
            </div>
          </div>
        </div>
      );
    });
  }, [messages, formatMessageContent, t]);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      if (checkIfAtBottom.cancel) {
        checkIfAtBottom.cancel();
      }
      
      cleanup.cleanup();
      hasLoadedHistoryRef.current = false;
    };
  }, [checkIfAtBottom, cleanup]);

  if (loadingHistory) {
    return (
      <div className="flex flex-col h-full bg-gray-800">
        <div className="px-4 py-3 border-b border-gray-700">
          <h3 className="text-base font-semibold text-white">{t('chat.title')}</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-400 text-sm">{t('chat.loadingHistory')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700">
        <h3 className="text-base font-semibold text-white">{t('chat.title')}</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          {t('chat.description')}
        </p>
      </div>

      {/* DROP ZONE - wraps the entire messages area */}
      <div 
        ref={dropZoneRef}
        className={`flex-grow flex flex-col overflow-hidden relative ${
          isDragging ? 'bg-cyan-900/20 border-4 border-dashed border-cyan-400' : ''
        }`}
        data-dropzone="session-chat"
      >
        {isDragging && (
          <div className="absolute inset-0 bg-cyan-900/90 flex items-center justify-center z-50 pointer-events-none">
            <div className="text-center">
              <Upload className="w-16 h-16 text-cyan-400 mx-auto mb-4 animate-bounce" />
              <p className="text-2xl font-bold text-white mb-2">{t('chat.dropZone.title')}</p>
              <p className="text-lg text-cyan-200">{t('chat.dropZone.subtitle')}</p>
              <p className="text-sm text-gray-300 mt-2">{t('chat.dropZone.maxSize')}</p>
            </div>
          </div>
        )}

        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-4 py-3 min-h-0"
          onScroll={checkIfAtBottom}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(75, 85, 99, 0.5) rgba(31, 41, 55, 0.3)',
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        >
          {/* column-reverse: visual bottom = scrollTop 0, new messages auto-appear at bottom */}
          <div ref={messagesEndRef} />
          {messages.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <p className="text-sm">{t('chat.noMessages')}</p>
              <p className="text-xs mt-1">{t('chat.beFirst')}</p>
            </div>
          ) : (
            renderMessagesReversed()
          )}
        </div>

        {/* Unread indicator - inside drop zone */}
        {unreadCount > 0 && !isAtBottomRef.current && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <button
              onClick={() => scrollToBottom(true)}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg flex items-center space-x-2 transition-colors"
            >
              <span>{t('chat.newMessages', { count: unreadCount })}</span>
              <span>↓</span>
            </button>
          </div>
        )}
      </div>


      {/* Session chat hint */}
      <div className="px-3 py-1.5 border-t border-gray-700">
        <p className="text-xs text-gray-500 text-center">
          {t('chat.sessionChatHint')}
        </p>
      </div>

      {/* Input - outside drop zone */}
      <form onSubmit={handleSendMessage} className="px-3 py-2 border-t border-gray-700">
        {/* File attachment preview */}
        {attachedFile && (
          <div className="mb-2 p-2 bg-gray-700 rounded flex items-center">
            <File className="w-5 h-5 text-gray-300 flex-shrink-0" />
            <div className="flex-1 min-w-0 ml-2">
              <p className="text-xs font-medium text-white truncate">{attachedFile.name}</p>
              <p className="text-xs text-gray-400">{(attachedFile.size / 1024).toFixed(0)} KB</p>
            </div>
            <button
              type="button"
              onClick={removeAttachedFile}
              className="ml-2 p-0.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload progress */}
        {isUploadingFile && (
          <div className="mb-2">
            <div className="bg-gray-700 rounded-full h-1.5">
              <div 
                className="bg-cyan-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('chat.uploading', { progress: uploadProgress })}</p>
          </div>
        )}

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700 transition-colors flex-shrink-0"
            title={t('chat.attachFile')}
            disabled={sendingMessage || isUploadingFile}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="*/*"
          />
          
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={handleTyping}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            placeholder={attachedFile ? t('chat.inputPlaceholderWithFile') : t('chat.inputPlaceholder')}
            disabled={sendingMessage || isUploadingFile}
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50"
            style={{ minHeight: '38px' }}
          />
          
          <button
            type="submit"
            disabled={(!newMessage.trim() && !attachedFile) || sendingMessage || isUploadingFile}
            className={`p-2 rounded-lg transition-all flex-shrink-0 ${
              (newMessage.trim() || attachedFile) && !sendingMessage && !isUploadingFile 
                ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm' 
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            title={t('chat.sendMessage')}
          >
            {sendingMessage || isUploadingFile ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowUpIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
});

SessionChat.displayName = 'SessionChat';

export default SessionChat;